/**
 * HTTP API Client for Myth-Kit backend (Worker or Fastify).
 */

import {
  getEffectiveViteApiUrl,
  NGROK_HOST_PATTERN,
  DEFAULT_PUBLIC_API_ORIGIN,
  resolveApiBaseUrl,
} from '@/utils/apiUrlEnv';
import { isAuthError } from '@/utils/apiErrors';
import { logCampaignData, shouldLogCampaignApiUrl } from '@/utils/campaignDataDebug';

/**
 * Automatically detect the API server URL based on the current environment
 * Priority:
 * 1. VITE_API_URL environment variable (highest priority)
 * 2. localStorage override (for manual configuration)
 * 3. Auto-detect from current hostname (for mobile/network access)
 * 4. Try server discovery (if app is served from the same server)
 * 5. Return null to require configuration (no localhost fallback)
 */
async function discoverServerUrl() {
  // Try to discover server from common locations
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;
    
    // Try discovery endpoints in order of likelihood
    const discoveryUrls = [
      // Same origin (if app is served from the server)
      `${protocol}//${hostname}${port ? `:${port}` : ''}/api/config`,
      // Common server port
      `${protocol}//${hostname}:3000/api/config`,
      // If accessed via IP, try that IP with port 3000
      hostname !== 'localhost' && hostname !== '127.0.0.1' 
        ? `${protocol}//${hostname}:3000/api/config`
        : null,
    ].filter(Boolean);

    for (const discoveryUrl of discoveryUrls) {
      try {
        // Create timeout controller for discovery
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        
        const response = await fetch(discoveryUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          // Check if response is JSON
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const config = await response.json();
            if (config.serverUrl) {
              return config.serverUrl;
            }
          } else {
            // Not JSON, skip this discovery URL
            continue;
          }
        }
      } catch (error) {
        // Discovery failed, try next URL
        continue;
      }
    }
  }
  
  return null;
}

function getApiBaseUrl() {
  // Always use the build-time API URL so all platforms hit the same backend.
  // This avoids localStorage overrides or auto-discovery creating split environments.
  const viteUrl = getEffectiveViteApiUrl();
  const chosen = viteUrl || DEFAULT_PUBLIC_API_ORIGIN;
  return resolveApiBaseUrl(chosen) || chosen;
}

const API_BASE_URL = getApiBaseUrl();

class ApiClient {
  constructor(baseURL = API_BASE_URL) {
    // If no base URL is configured, we'll handle it in the request method
    this.baseURL = baseURL != null && baseURL !== '' ? resolveApiBaseUrl(baseURL) ?? baseURL : baseURL;
    this.isDiscovering = false;
    /** @type {Map<string, Promise<any>>} In-flight GET requests by key to deduplicate concurrent identical requests */
    this._pendingGet = new Map();
  }

  /**
   * Check if server URL is configured
   * @returns {boolean} True if server URL is configured
   */
  isConfigured() {
    return this.baseURL !== null && this.baseURL !== undefined;
  }

  /**
   * Attempt to discover the server URL automatically
   * @returns {Promise<string|null>} Discovered server URL or null
   */
  async discoverServer() {
    if (this.isDiscovering) {
      return null; // Already discovering
    }

    this.isDiscovering = true;
    try {
      const discoveredUrl = await discoverServerUrl();
      if (discoveredUrl) {
        this.setBaseUrl(discoveredUrl);
        return discoveredUrl;
      }
    } catch (error) {
      // Server discovery failed
    } finally {
      this.isDiscovering = false;
    }
    
    return null;
  }

  /**
   * Update the API base URL (useful for runtime configuration)
   * @param {string} newUrl - The new API base URL
   */
  setBaseUrl(newUrl) {
    const resolved =
      newUrl != null && newUrl !== '' ? resolveApiBaseUrl(String(newUrl)) ?? String(newUrl).trim() : newUrl;
    this.baseURL = resolved;
    // Optionally save to localStorage for persistence
    if (resolved) {
      localStorage.setItem('api_server_url', resolved);
    } else {
      localStorage.removeItem('api_server_url');
    }
  }

  /**
   * Get the current API base URL
   * @returns {string} The current API base URL
   */
  getBaseUrl() {
    if (this.baseURL == null || this.baseURL === '') return this.baseURL;
    return resolveApiBaseUrl(this.baseURL) ?? this.baseURL;
  }

  async request(endpoint, options = {}) {
    // Check if server URL is configured, try discovery if not
    if (!this.baseURL) {
      // Try to discover server automatically
      const discoveredUrl = await this.discoverServer();
      
      if (!discoveredUrl && !this.baseURL) {
        const errorMsg = `Server URL not configured.\n\n` +
          `🌐 Please configure remote server access:\n` +
          `1. Set up port forwarding or use a tunneling service (Cloudflare Tunnel, ngrok)\n` +
          `2. Click "Configure Server URL" to enter your server URL\n` +
          `3. Or set manually: localStorage.setItem('api_server_url', 'http://your-server:3000')`;
        
        const configError = new Error(errorMsg);
        configError.isConnectionError = true;
        configError.isConfigurationError = true;
        configError.serverUrl = null;
        throw configError;
      }
    }

    const base = resolveApiBaseUrl(this.baseURL) ?? this.baseURL;
    const url = `${base}${endpoint}`;
    const isGet = (options.method || 'GET').toUpperCase() === 'GET';
    const dedupeKey = isGet ? `GET:${url}` : null;

    if (dedupeKey && this._pendingGet.has(dedupeKey)) {
      return this._pendingGet.get(dedupeKey);
    }

    const runRequest = async () => {
    const protectedApiRequest =
      endpoint.startsWith('/api/') &&
      !endpoint.startsWith('/api/auth/login') &&
      !endpoint.startsWith('/api/auth/register') &&
      !endpoint.startsWith('/api/auth/forgot-password') &&
      !endpoint.startsWith('/api/auth/reset-password');

    // Anonymous (no JWT): do not mint guest tokens (M10). Attach token only if present.
    const token = localStorage.getItem('auth_token');

    const buildConfig = (authToken) => {
      const headers = {
        'Content-Type': 'application/json',
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
        ...options.headers,
      };
      if (base && NGROK_HOST_PATTERN.test(base)) {
        headers['ngrok-skip-browser-warning'] = 'true';
      }
      return {
        ...options,
        headers,
      };
    };

    const sendRequest = async (authToken) => {
      const response = await fetch(url, buildConfig(authToken));
      
      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      
      if (!response.ok) {
        let error;
        if (isJson) {
          error = await response.json().catch(() => ({ message: response.statusText }));
        } else {
          // Response is HTML or other format (likely an error page)
          const text = await response.text();
          
          // Provide helpful error message
          if (response.status === 404) {
            error = { 
              message: `Server endpoint not found. Check that your server URL is correct: ${base}` 
            };
          } else if (response.status === 502 || response.status === 503) {
            error = { 
              message: `Server is not responding. Verify the server is running and accessible at: ${base}` 
            };
          } else {
            error = { 
              message: `Server returned HTML instead of JSON. Status: ${response.status}. Check server URL: ${base}` 
            };
          }
        }
        
        const errorMessage = error.message || error.error || response.statusText || `HTTP ${response.status}`;
        const errorWithStatus = new Error(errorMessage);
        errorWithStatus.status = response.status;
        errorWithStatus.statusText = response.statusText;
        errorWithStatus.isAuthError = isAuthError({ message: errorMessage, status: response.status });

        if (shouldLogCampaignApiUrl(url)) {
          logCampaignData('api-client', `${(options.method || 'GET').toUpperCase()} failed ${response.status}`, {
            url,
            error: errorMessage,
            body: typeof error === 'object' ? error : undefined,
          });
        }

        throw errorWithStatus;
      }

      // Handle 204 No Content responses (no body to parse)
      if (response.status === 204) {
        return null;
      }

      // Check content type before parsing JSON
      if (!isJson) {
        const text = await response.text();
        throw new Error(`Server returned HTML/text instead of JSON. Check server URL: ${base}`);
      }

      const data = await response.json();
      if (shouldLogCampaignApiUrl(url)) {
        logCampaignData(
          'api-client',
          `${(options.method || 'GET').toUpperCase()} ok ${response.status}`,
          { url, data }
        );
      }
      return data;
    };

    try {
      return await sendRequest(token);
    } catch (error) {
      if (protectedApiRequest && error?.status === 401 && !options._skipAuthRetry) {
        // Clear invalid session; stay anonymous — do not mint a guest JWT (M10).
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('mythkit:auth-updated'));
        }
      }
      // Network error or connection timeout
      if (error.name === 'TypeError' || error.message?.includes('fetch') || error.message?.includes('ERR_CONNECTION_TIMED_OUT') || error.message?.includes('Failed to fetch')) {
        const serverUrl = base;
        const isLocalhost = serverUrl.includes('localhost') || serverUrl.includes('127.0.0.1');
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                        (typeof window !== 'undefined' && window.innerWidth < 768);
        
        // Provide helpful error messages based on the scenario
        let errorMsg;
        if (isLocalhost) {
          if (isMobile) {
            errorMsg = `Cannot connect to server at ${serverUrl}.\n\n` +
              `⚠️ You're accessing via localhost, which won't work on mobile devices.\n\n` +
              `📱 For same network (mobile on same WiFi):\n` +
              `1. Find your computer's IP address (check server console output)\n` +
              `2. Access the app using: http://YOUR_IP:5173 (or whatever port Vite uses)\n` +
              `3. The server URL will auto-detect to: http://YOUR_IP:3000\n\n` +
              `🌐 For remote access (different network):\n` +
              `1. Set up port forwarding or use tunneling (Cloudflare Tunnel, ngrok)\n` +
              `2. Click "Configure Server URL" below to enter the remote server URL`;
          } else {
            errorMsg = `Cannot connect to server at ${serverUrl}.\n\n` +
              `Make sure the server is running:\n` +
              `  npm run server\n\n` +
              `For mobile device access on the same network:\n` +
              `1. Access the app using your computer's IP: http://YOUR_IP:5173\n` +
              `2. The server URL will auto-detect correctly\n\n` +
              `For remote access, click "Configure Server URL" below.`;
          }
        } else {
          // Remote URL (e.g. https://app.myth-kit.com) — include tunnel tips when applicable
          const isTunnelDomain = /myth-kit\.com|trycloudflare\.com|cfargotunnel\.com|ngrok\.io|ngrok-free\.(dev|app)/i.test(serverUrl);
          errorMsg = `Cannot connect to server at ${serverUrl}.\n\n` +
            `Possible solutions:\n` +
            `1. Verify the server is running and accessible\n` +
            `2. Check your network connection\n` +
            `3. If using remote access, ensure port forwarding or tunneling is configured\n` +
            `4. Click "Configure Server URL" below to update the server address`;
          if (isTunnelDomain) {
            errorMsg += `\n\n🔧 If using Cloudflare Tunnel:\n` +
              `• Open ${serverUrl.replace(/\/$/, '')}/health in a browser — if you see JSON, the API is reachable from that device.\n` +
              `• In Cloudflare Zero Trust: ensure this hostname is a public hostname for your tunnel and the tunnel is running (e.g. \`cloudflared tunnel run mythkit\`).`;
          }
        }
        
        const connectionError = new Error(errorMsg);
        connectionError.isConnectionError = true;
        connectionError.serverUrl = serverUrl;
        throw connectionError;
      }
      // Don't re-throw if it's already an error with status (we've already logged it if needed)
      throw error;
    }
    }; // end runRequest

    const promise = runRequest();
    if (dedupeKey) {
      this._pendingGet.set(dedupeKey, promise);
      promise.finally(() => { this._pendingGet.delete(dedupeKey); });
    }
    return promise;
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Auth: forgot / reset password (no token required)
  async forgotPassword(email) {
    return this.post('/api/auth/forgot-password', { email });
  }

  async resetPassword(token, newPassword) {
    return this.post('/api/auth/reset-password', { token, newPassword });
  }

  async reportBug(payload) {
    return this.post('/api/bugs/report', payload);
  }

  /** One-time migration: get full datapack (characters, campaigns, homebrew) from server */
  async getExportMyData() {
    return this.get('/api/auth/export-my-data');
  }

  /** Delete current user account and all associated data. Requires auth. */
  async deleteAccount() {
    return this.delete('/api/auth/delete-account');
  }

  // Health check
  async health() {
    return this.get('/health');
  }

  /**
   * Get client cache version and toggles from server. No auth required.
   * On network error returns safe default so app still works and no wipe runs when server is unreachable.
   * @returns {Promise<{ clientDataVersion: number, cacheVersionEnabled: boolean, serverAuthoritative: boolean }>}
   */
  async getMeta() {
    const defaultMeta = {
      clientDataVersion: 0,
      cacheVersionEnabled: false,
      serverAuthoritative: false,
    };
    if (!this.baseURL) return defaultMeta;
    try {
      const base = resolveApiBaseUrl(this.baseURL) ?? this.baseURL;
      const url = `${base}/api/meta`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return defaultMeta;
      const data = await res.json();
      return {
        clientDataVersion: typeof data.clientDataVersion === 'number' ? data.clientDataVersion : defaultMeta.clientDataVersion,
        cacheVersionEnabled: data.cacheVersionEnabled === true,
        serverAuthoritative: data.serverAuthoritative !== false,
      };
    } catch {
      return defaultMeta;
    }
  }

  // Character endpoints
  async getCharacters(all = false) {
    const url = all ? '/api/characters?all=true' : '/api/characters';
    return this.get(url);
  }

  async getCharacter(id, options = {}) {
    const q =
      options.campaignId != null && String(options.campaignId).trim() !== ''
        ? `?campaignId=${encodeURIComponent(String(options.campaignId).trim())}`
        : '';
    return this.get(`/api/characters/${id}${q}`);
  }

  async getCharacterJournal(characterId, campaignId) {
    const q =
      campaignId != null && String(campaignId).trim() !== ''
        ? `?campaignId=${encodeURIComponent(String(campaignId).trim())}`
        : '';
    return this.get(`/api/characters/${characterId}/journal${q}`);
  }

  async createCharacterJournal(characterId, data) {
    return this.post(`/api/characters/${characterId}/journal`, data);
  }

  async updateCharacterJournal(characterId, entryId, data) {
    return this.put(`/api/characters/${characterId}/journal/${entryId}`, data);
  }

  async deleteCharacterJournal(characterId, entryId) {
    return this.delete(`/api/characters/${characterId}/journal/${entryId}`);
  }

  async patchCampaignCharacterSheetVisibility(campaignId, characterId, payload) {
    return this.patch(
      `/api/campaigns/${campaignId}/characters/${characterId}/link-visibility`,
      payload
    );
  }

  async createCharacter(data) {
    return this.post('/api/characters', data);
  }

  async importCharacterByShareCode(code) {
    return this.post('/api/characters/import-share-code', { code });
  }

  async updateCharacter(id, data) {
    return this.put(`/api/characters/${id}`, data);
  }

  async deleteCharacter(id) {
    return this.delete(`/api/characters/${id}`);
  }

  // Campaign endpoints
  async getCampaigns() {
    return this.get('/api/campaigns');
  }

  async getCampaign(id) {
    return this.get(`/api/campaigns/${id}`);
  }

  async createCampaign(data) {
    return this.post('/api/campaigns', data);
  }

  async updateCampaign(id, data) {
    return this.put(`/api/campaigns/${id}`, data);
  }

  async deleteCampaign(id) {
    return this.delete(`/api/campaigns/${id}`);
  }

  async invitePlayer(campaignId, email, role = 'Player') {
    return this.post(`/api/campaigns/${campaignId}/invite`, { email, role });
  }

  /** Public: get campaign name for join page (no auth required). */
  async getCampaignJoinInfo(campaignId, token) {
    return this.get(`/api/campaigns/${campaignId}/join-info?token=${encodeURIComponent(token)}`);
  }

  /** Join campaign using share link token (auth required). */
  async joinCampaign(campaignId, { token, character_id }) {
    return this.post(`/api/campaigns/${campaignId}/join`, { token, character_id });
  }

  /** Get shareable join URL (DM only). */
  async getCampaignJoinLink(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/join-link`);
  }

  // Campaign chat endpoints
  async getCampaignChat(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/chat`);
  }

  async sendCampaignChatMessage(campaignId, message, mentionUserIds = [], options = {}) {
    const body = { message };
    if (Array.isArray(mentionUserIds) && mentionUserIds.length > 0) {
      body.mention_user_ids = mentionUserIds;
    }
    if (options.visibility) body.visibility = options.visibility;
    if (options.whisper_to_user_id != null) body.whisper_to_user_id = options.whisper_to_user_id;
    return this.post(`/api/campaigns/${campaignId}/chat`, body);
  }

  async updateCampaignChatMessage(campaignId, messageId, message) {
    return this.patch(`/api/campaigns/${campaignId}/chat/${messageId}`, { message });
  }

  async deleteCampaignChatMessage(campaignId, messageId) {
    return this.delete(`/api/campaigns/${campaignId}/chat/${messageId}`);
  }

  async reactCampaignChatMessage(campaignId, messageId, emoji) {
    return this.post(`/api/campaigns/${campaignId}/chat/${messageId}/reactions`, { emoji });
  }

  // Campaign presence (online/offline heartbeat for chat)
  async getCampaignPresence(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/presence`);
  }

  async sendCampaignPresenceHeartbeat(campaignId, status = 'online') {
    return this.post(`/api/campaigns/${campaignId}/presence/heartbeat`, { status });
  }

  // Campaign notes endpoints
  async getCampaignNotes(campaignId) {
    try {
      console.debug('[CampaignNotes] fetch:start', { campaignId });
      const result = await this.get(`/api/campaigns/${campaignId}/notes`);
      console.debug('[CampaignNotes] fetch:success', {
        campaignId,
        count: Array.isArray(result) ? result.length : null,
      });
      return result;
    } catch (error) {
      console.error('[CampaignNotes] fetch:error', { campaignId, error });
      throw error;
    }
  }

  async createCampaignNote(campaignId, data) {
    try {
      console.debug('[CampaignNotes] create:start', {
        campaignId,
        hasTitle: Boolean(data?.title && String(data.title).trim()),
        visibility: data?.visibility ?? 'party',
      });
      const result = await this.post(`/api/campaigns/${campaignId}/notes`, data);
      console.debug('[CampaignNotes] create:success', {
        campaignId,
        noteId: result?.id ?? null,
      });
      return result;
    } catch (error) {
      console.error('[CampaignNotes] create:error', { campaignId, data, error });
      throw error;
    }
  }

  async updateCampaignNote(campaignId, noteId, data) {
    try {
      console.debug('[CampaignNotes] update:start', {
        campaignId,
        noteId,
        hasTitle: data?.title != null,
      });
      const result = await this.put(`/api/campaigns/${campaignId}/notes/${noteId}`, data);
      console.debug('[CampaignNotes] update:success', { campaignId, noteId });
      return result;
    } catch (error) {
      console.error('[CampaignNotes] update:error', { campaignId, noteId, data, error });
      throw error;
    }
  }

  async deleteCampaignNote(campaignId, noteId) {
    try {
      console.debug('[CampaignNotes] delete:start', { campaignId, noteId });
      const result = await this.delete(`/api/campaigns/${campaignId}/notes/${noteId}`);
      console.debug('[CampaignNotes] delete:success', { campaignId, noteId });
      return result;
    } catch (error) {
      console.error('[CampaignNotes] delete:error', { campaignId, noteId, error });
      throw error;
    }
  }

  async updateNoteVisibility(campaignId, noteId, visibility, targetUserIds = []) {
    return this.patch(`/api/campaigns/${campaignId}/notes/${noteId}/visibility`, {
      visibility,
      target_user_ids: targetUserIds,
    });
  }

  async hideNote(campaignId, noteId, isHidden) {
    try {
      console.debug('[CampaignNotes] hide:start', { campaignId, noteId, isHidden });
      const result = await this.patch(`/api/campaigns/${campaignId}/notes/${noteId}/hide`, {
        is_hidden: isHidden,
      });
      console.debug('[CampaignNotes] hide:success', { campaignId, noteId, isHidden });
      return result;
    } catch (error) {
      console.error('[CampaignNotes] hide:error', { campaignId, noteId, isHidden, error });
      throw error;
    }
  }

  // Campaign members endpoints
  async getCampaignMembers(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/members`);
  }

  async removeCampaignMember(campaignId, userId) {
    return this.delete(`/api/campaigns/${campaignId}/members/${userId}`);
  }

  async updateCampaignMember(campaignId, userId, data) {
    return this.patch(`/api/campaigns/${campaignId}/members/${userId}`, data);
  }

  async promoteCampaignMember(campaignId, userId) {
    return this.post(`/api/campaigns/${campaignId}/members/${userId}/promote`);
  }

  // Campaign activity endpoint
  async getCampaignActivity(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/activity`);
  }

  /** Typed campaign activity (hp_change, condition, rest, death_save) — not dice spoofs. */
  async logCampaignActivity(campaignId, { type, data = {}, summary = null, character_id = null } = {}) {
    return this.post(`/api/campaigns/${campaignId}/activity`, {
      type,
      data,
      summary,
      character_id,
    });
  }

  // NPC endpoints
  async getNPCs(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.is_combat_npc !== undefined) params.append('is_combat_npc', filters.is_combat_npc);
    const query = params.toString();
    return this.get(`/api/campaigns/${campaignId}/npcs${query ? `?${query}` : ''}`);
  }

  async createNPC(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/npcs`, data);
  }

  async updateNPC(campaignId, npcId, data) {
    return this.put(`/api/campaigns/${campaignId}/npcs/${npcId}`, data);
  }

  async deleteNPC(campaignId, npcId) {
    return this.delete(`/api/campaigns/${campaignId}/npcs/${npcId}`);
  }

  async generateNPC(campaignId, npcType, generationType) {
    return this.post(`/api/campaigns/${campaignId}/npcs/generate`, {
      npc_type: npcType,
      generation_type: generationType,
    });
  }

  async getNPCRollableSheet(campaignId, npcId) {
    return this.get(`/api/campaigns/${campaignId}/npcs/${npcId}/rollable-sheet`);
  }

  // Shop endpoints
  async getShops(campaignId, locationId = null) {
    const query = locationId ? `?location_id=${locationId}` : '';
    return this.get(`/api/campaigns/${campaignId}/shops${query}`);
  }

  async createShop(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/shops`, data);
  }

  async updateShop(campaignId, shopId, data) {
    return this.put(`/api/campaigns/${campaignId}/shops/${shopId}`, data);
  }

  async deleteShop(campaignId, shopId) {
    return this.delete(`/api/campaigns/${campaignId}/shops/${shopId}`);
  }

  async getShopInventory(campaignId, shopId) {
    return this.get(`/api/campaigns/${campaignId}/shops/${shopId}/inventory`);
  }

  async addShopItem(campaignId, shopId, data) {
    return this.post(`/api/campaigns/${campaignId}/shops/${shopId}/items`, data);
  }

  async updateShopItem(campaignId, shopId, itemId, data) {
    return this.put(`/api/campaigns/${campaignId}/shops/${shopId}/items/${itemId}`, data);
  }

  async restockShop(campaignId, shopId) {
    return this.post(`/api/campaigns/${campaignId}/shops/${shopId}/restock`);
  }

  async deleteShopItem(campaignId, shopId, itemId) {
    return this.delete(`/api/campaigns/${campaignId}/shops/${shopId}/items/${itemId}`);
  }

  async addShopItemsBulk(campaignId, shopId, { items, mode = 'append' }) {
    return this.post(`/api/campaigns/${campaignId}/shops/${shopId}/items/bulk`, { items, mode });
  }

  async getShopPurchases(campaignId, shopId) {
    return this.get(`/api/campaigns/${campaignId}/shops/${shopId}/purchases`);
  }

  async purchaseFromShop(campaignId, shopId, { character_id, payment_source, lines }) {
    return this.post(`/api/campaigns/${campaignId}/shops/${shopId}/purchase`, {
      character_id,
      payment_source,
      lines,
    });
  }

  async generateShop(campaignId, shopType, locationId = null) {
    return this.post(`/api/campaigns/${campaignId}/shops/generate`, {
      shop_type: shopType,
      location_id: locationId,
    });
  }

  async getSessionSurfaces(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/session-surfaces`);
  }

  async deploySessionSurface(campaignId, body) {
    return this.post(`/api/campaigns/${campaignId}/session-surfaces/deploy`, body);
  }

  async undeploySessionSurface(campaignId, surfaceId, body = {}) {
    return this.post(`/api/campaigns/${campaignId}/session-surfaces/${surfaceId}/undeploy`, body);
  }

  async patchSessionSurface(campaignId, surfaceId, body) {
    return this.patch(`/api/campaigns/${campaignId}/session-surfaces/${surfaceId}`, body);
  }

  async openAllShopsAtLocation(campaignId, locationId) {
    return this.post(`/api/campaigns/${campaignId}/locations/${locationId}/shops/open-all`, {});
  }

  async closeAllShopsAtLocation(campaignId, locationId) {
    return this.post(`/api/campaigns/${campaignId}/locations/${locationId}/shops/close-all`, {});
  }

  async getShopOffers(campaignId, status = null) {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.get(`/api/campaigns/${campaignId}/shop-offers${q}`);
  }

  async createShopOffer(campaignId, shopId, body) {
    return this.post(`/api/campaigns/${campaignId}/shops/${shopId}/offers`, body);
  }

  async respondToShopOffer(campaignId, shopId, offerId, body) {
    return this.post(`/api/campaigns/${campaignId}/shops/${shopId}/offers/${offerId}/respond`, body);
  }

  async applyStarterPack(campaignId) {
    return this.post(`/api/campaigns/${campaignId}/starter-pack`, {});
  }

  // Character linking endpoints
  async linkCharacter(campaignId, characterId) {
    return this.post(`/api/campaigns/${campaignId}/characters/link`, { character_id: characterId });
  }

  /** Create a character for the current user and link it to the campaign (for local-only characters). */
  async createCampaignCharacterAndLink(campaignId, payload) {
    return this.post(`/api/campaigns/${campaignId}/characters`, payload);
  }

  async getLinkedCharacters(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/characters`);
  }

  /** Get a campaign member's characters (for assigning to campaign). DM or the member. */
  async getMemberCharacters(campaignId, userId) {
    return this.get(`/api/campaigns/${campaignId}/members/${userId}/characters`);
  }

  /** Sync current user's local characters to the server so the DM can assign them (e.g. when player opens Members). */
  async syncMyCharactersToCampaign(campaignId, characters) {
    return this.post(`/api/campaigns/${campaignId}/members/me/sync-characters`, { characters });
  }

  /** Sync current user's characters to the server (no campaign). Use when online after create/update. */
  async syncMyCharactersToServer(characters) {
    return this.post('/api/me/sync-characters', { characters });
  }

  /** @returns {Promise<{ push_preferences: { sessions: boolean, campaign: boolean, chat_mentions: boolean, chat_all: boolean, activity: boolean, billing: boolean } }>} */
  async getPushPreferences() {
    return this.get('/api/me/push-preferences');
  }

  /** @param {Partial<{ sessions: boolean, campaign: boolean, chat_mentions: boolean, chat_all: boolean, activity: boolean, billing: boolean }>} partial */
  async patchPushPreferences(partial) {
    return this.patch('/api/me/push-preferences', partial);
  }

  async unlinkCharacter(campaignId, characterId) {
    return this.delete(`/api/campaigns/${campaignId}/characters/${characterId}/unlink`);
  }

  // DM Notes endpoints
  async getDMNotes(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/dm-notes`);
  }

  async createDMNote(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/dm-notes`, data);
  }

  async updateDMNote(campaignId, noteId, data) {
    return this.put(`/api/campaigns/${campaignId}/dm-notes/${noteId}`, data);
  }

  async deleteDMNote(campaignId, noteId) {
    return this.delete(`/api/campaigns/${campaignId}/dm-notes/${noteId}`);
  }

  // Timeline endpoints
  async getTimeline(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/timeline`);
  }

  async createTimelineEvent(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/timeline/events`, data);
  }

  async updateTimelineEvent(campaignId, eventId, data) {
    return this.put(`/api/campaigns/${campaignId}/timeline/events/${eventId}`, data);
  }

  async deleteTimelineEvent(campaignId, eventId) {
    return this.delete(`/api/campaigns/${campaignId}/timeline/events/${eventId}`);
  }

  // Universal entity platform
  async searchCampaignEntities(campaignId, params = {}) {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.types) q.set('types', params.types);
    if (params.tags) q.set('tags', params.tags);
    const qs = q.toString();
    return this.get(`/api/campaigns/${campaignId}/entities${qs ? `?${qs}` : ''}`);
  }

  async getEntityDetail(campaignId, entityType, entityId) {
    return this.get(`/api/campaigns/${campaignId}/entities/${entityType}/${entityId}`);
  }

  async getEntityRelationships(campaignId, params = {}) {
    const q = new URLSearchParams();
    if (params.from_type) q.set('from_type', params.from_type);
    if (params.from_id) q.set('from_id', params.from_id);
    if (params.to_type) q.set('to_type', params.to_type);
    if (params.to_id) q.set('to_id', params.to_id);
    const qs = q.toString();
    return this.get(`/api/campaigns/${campaignId}/relationships${qs ? `?${qs}` : ''}`);
  }

  async createEntityRelationship(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/relationships`, data);
  }

  async deleteEntityRelationship(campaignId, relationshipId) {
    return this.delete(`/api/campaigns/${campaignId}/relationships/${relationshipId}`);
  }

  async getEntityTags(campaignId, entityType, entityId) {
    return this.get(`/api/campaigns/${campaignId}/entities/${entityType}/${entityId}/tags`);
  }

  async setEntityTags(campaignId, entityType, entityId, tags) {
    return this.put(`/api/campaigns/${campaignId}/entities/${entityType}/${entityId}/tags`, { tags });
  }

  async setEntityReveal(campaignId, entityType, entityId, data) {
    return this.put(`/api/campaigns/${campaignId}/entities/${entityType}/${entityId}/reveal`, data);
  }

  // Combat endpoints
  async getCampaignCombats(campaignId, active = false) {
    const query = active ? '?active=true' : '';
    return this.get(`/api/campaigns/${campaignId}/combats${query}`);
  }

  async createCombat(campaignId, name, extra = {}) {
    return this.post(`/api/campaigns/${campaignId}/combats`, { name, ...extra });
  }

  async getCombat(campaignId, combatId) {
    return this.get(`/api/campaigns/${campaignId}/combats/${combatId}`);
  }

  async updateCombat(campaignId, combatId, data) {
    return this.put(`/api/campaigns/${campaignId}/combats/${combatId}`, data);
  }

  async updateParticipantHealth(campaignId, combatId, participantId, data) {
    return this.put(`/api/campaigns/${campaignId}/combats/${combatId}/participants/${participantId}/health`, data);
  }

  async updateParticipantInitiative(campaignId, combatId, participantId, initiative) {
    return this.put(`/api/campaigns/${campaignId}/combats/${combatId}/participants/${participantId}/initiative`, { initiative });
  }

  async updateParticipantAC(campaignId, combatId, participantId, armor_class) {
    return this.put(`/api/campaigns/${campaignId}/combats/${combatId}/participants/${participantId}/ac`, { armor_class });
  }

  async deleteParticipant(campaignId, combatId, participantId) {
    return this.delete(`/api/campaigns/${campaignId}/combats/${combatId}/participants/${participantId}`);
  }

  async getCombatParticipants(campaignId, combatId, type = null) {
    const query = type ? `?type=${type}` : '';
    return this.get(`/api/campaigns/${campaignId}/combats/${combatId}/participants${query}`);
  }

  async addCampaignCombatParticipant(campaignId, combatId, participantData) {
    return this.post(`/api/campaigns/${campaignId}/combats/${combatId}/participants`, participantData);
  }

  async addCombatParticipant(participantData) {
    return this.post('/api/combatParticipants', participantData);
  }

  async syncInitiative(campaignId, combatId, characterId, initiative) {
    return this.post(`/api/campaigns/${campaignId}/combats/${combatId}/sync-initiative`, {
      character_id: characterId,
      initiative,
    });
  }

  async saveCombatState(campaignId, combatId, name, description) {
    return this.post(`/api/campaigns/${campaignId}/combats/${combatId}/save`, {
      name,
      description,
    });
  }

  async loadCombatState(campaignId, encounterId) {
    return this.post(`/api/campaigns/${campaignId}/combats/load`, {
      encounter_id: encounterId,
    });
  }

  // Real-time updates
  async getCampaignUpdates(campaignId, since = null) {
    const query = since ? `?since=${since}` : '';
    return this.get(`/api/campaigns/${campaignId}/updates${query}`);
  }

  /**
   * Get WebSocket URL for a campaign
   * @param {string} campaignId - Campaign ID
   * @param {string} token - JWT token
   * @returns {string} WebSocket URL
   */
  getWebSocketUrl(campaignId, token) {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('API base URL not configured');
    }

    // Convert http:// to ws:// and https:// to wss://
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const wsBaseUrl = baseUrl.replace(/^https?/, wsProtocol);
    
    return `${wsBaseUrl}/api/campaigns/${campaignId}/ws?token=${encodeURIComponent(token)}`;
  }

  // Statistics endpoints
  async getCampaignStatistics(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/statistics`);
  }

  async getCombatStatistics(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/statistics/combat`);
  }

  async getCharacterStatistics(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/statistics/characters`);
  }

  async getCampaignDashboard(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/dashboard`);
  }

  // Dice rolling endpoints
  async rollDice(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/dice/roll`, data);
  }

  async getDiceRolls(campaignId, limit = 50) {
    return this.get(`/api/campaigns/${campaignId}/dice/rolls?limit=${limit}`);
  }

  // Export/Import endpoints
  async exportCampaignJSON(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/export/json`, {
      responseType: 'blob',
    });
  }

  async importCampaignData(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/import`, { data });
  }

  // Session endpoints
  async getCampaignSessions(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    const queryString = params.toString();
    return this.get(`/api/campaigns/${campaignId}/sessions${queryString ? `?${queryString}` : ''}`);
  }

  async createSession(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/sessions`, data);
  }

  async updateSession(campaignId, sessionId, data) {
    return this.put(`/api/campaigns/${campaignId}/sessions/${sessionId}`, data);
  }

  async deleteSession(campaignId, sessionId) {
    return this.delete(`/api/campaigns/${campaignId}/sessions/${sessionId}`);
  }

  async updateSessionRSVP(campaignId, sessionId, status, notes = null) {
    return this.post(`/api/campaigns/${campaignId}/sessions/${sessionId}/rsvp`, { status, notes });
  }

  // Progressive Reveal endpoints
  async updateNPCReveal(campaignId, npcId, reveal_level, revealed_to_users = []) {
    return this.put(`/api/campaigns/${campaignId}/npcs/${npcId}/reveal`, { reveal_level, revealed_to_users });
  }

  async updateLocationReveal(campaignId, locationId, reveal_level, revealed_to_users = []) {
    return this.put(`/api/campaigns/${campaignId}/locations/${locationId}/reveal`, { reveal_level, revealed_to_users });
  }

  async updateHandoutReveal(campaignId, handoutId, reveal_level, revealed_to_users = []) {
    return this.put(`/api/campaigns/${campaignId}/handouts/${handoutId}/reveal`, { reveal_level, revealed_to_users });
  }

  // Session Snapshot endpoints
  async getSessionSnapshots(campaignId, sessionId = null) {
    const params = sessionId ? `?session_id=${sessionId}` : '';
    return this.get(`/api/campaigns/${campaignId}/snapshots${params}`);
  }

  async createSessionSnapshot(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/snapshots`, data);
  }

  async restoreSessionSnapshot(campaignId, snapshotId) {
    return this.post(`/api/campaigns/${campaignId}/snapshots/${snapshotId}/restore`);
  }

  async deleteSessionSnapshot(campaignId, snapshotId) {
    return this.delete(`/api/campaigns/${campaignId}/snapshots/${snapshotId}`);
  }

  // Roll Log endpoints
  async getRollLog(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.session_id) params.append('session_id', filters.session_id);
    if (filters.character_id) params.append('character_id', filters.character_id);
    if (filters.combat_id) params.append('combat_id', filters.combat_id);
    if (filters.roll_type) params.append('roll_type', filters.roll_type);
    if (filters.pinned_only) params.append('pinned_only', filters.pinned_only);
    if (filters.limit) params.append('limit', filters.limit);
    const queryString = params.toString();
    return this.get(`/api/campaigns/${campaignId}/rolls${queryString ? `?${queryString}` : ''}`);
  }

  async logRoll(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/rolls`, data);
  }

  async pinRoll(campaignId, rollId, isPinned) {
    return this.put(`/api/campaigns/${campaignId}/rolls/${rollId}/pin`, { is_pinned: isPinned });
  }

  async deleteRoll(campaignId, rollId) {
    return this.delete(`/api/campaigns/${campaignId}/rolls/${rollId}`);
  }

  // Location endpoints
  async getCampaignLocations(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/locations`);
  }

  async createLocation(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/locations`, data);
  }

  async updateLocation(campaignId, locationId, data) {
    return this.put(`/api/campaigns/${campaignId}/locations/${locationId}`, data);
  }

  async deleteLocation(campaignId, locationId) {
    return this.delete(`/api/campaigns/${campaignId}/locations/${locationId}`);
  }

  async linkEntityToLocation(campaignId, locationId, entityType, entityId) {
    return this.post(`/api/campaigns/${campaignId}/locations/${locationId}/link`, {
      entity_type: entityType,
      entity_id: entityId,
    });
  }

  // Quest endpoints
  async getCampaignQuests(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    const query = params.toString();
    return this.get(`/api/campaigns/${campaignId}/quests${query ? `?${query}` : ''}`);
  }

  async createQuest(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/quests`, data);
  }

  async updateQuest(campaignId, questId, data) {
    return this.put(`/api/campaigns/${campaignId}/quests/${questId}`, data);
  }

  async deleteQuest(campaignId, questId) {
    return this.delete(`/api/campaigns/${campaignId}/quests/${questId}`);
  }

  // Wiki endpoints
  async getCampaignWiki(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/wiki`);
  }

  async createWikiPage(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/wiki`, data);
  }

  async updateWikiPage(campaignId, pageId, data) {
    return this.put(`/api/campaigns/${campaignId}/wiki/${pageId}`, data);
  }

  async deleteWikiPage(campaignId, pageId) {
    return this.delete(`/api/campaigns/${campaignId}/wiki/${pageId}`);
  }

  // Handout endpoints
  async getCampaignHandouts(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/handouts`);
  }

  async createHandout(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/handouts`, data);
  }

  async updateHandout(campaignId, handoutId, data) {
    return this.put(`/api/campaigns/${campaignId}/handouts/${handoutId}`, data);
  }

  async deleteHandout(campaignId, handoutId) {
    return this.delete(`/api/campaigns/${campaignId}/handouts/${handoutId}`);
  }

  // Campaign maps (durable fog/pins/tokens)
  async getCampaignMaps(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/maps`);
  }

  async getCampaignMap(campaignId, mapId) {
    return this.get(`/api/campaigns/${campaignId}/maps/${mapId}`);
  }

  async createCampaignMap(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/maps`, data);
  }

  async updateCampaignMap(campaignId, mapId, data) {
    return this.patch(`/api/campaigns/${campaignId}/maps/${mapId}`, data);
  }

  async deleteCampaignMap(campaignId, mapId) {
    return this.delete(`/api/campaigns/${campaignId}/maps/${mapId}`);
  }

  async activateCampaignMap(campaignId, mapId) {
    return this.post(`/api/campaigns/${campaignId}/maps/${mapId}/activate`, {});
  }

  // Encounter endpoints
  async getCampaignEncounters(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.status) params.append('status', filters.status);
    const query = params.toString();
    return this.get(`/api/campaigns/${campaignId}/encounters${query ? `?${query}` : ''}`);
  }

  async addEncounterMonster(campaignId, encounterId, data) {
    return this.post(`/api/campaigns/${campaignId}/encounters/${encounterId}/monsters`, data);
  }

  async updateEncounterMonster(campaignId, encounterId, monsterId, data) {
    return this.put(`/api/campaigns/${campaignId}/encounters/${encounterId}/monsters/${monsterId}`, data);
  }

  async removeEncounterMonster(campaignId, encounterId, monsterId) {
    return this.delete(`/api/campaigns/${campaignId}/encounters/${encounterId}/monsters/${monsterId}`);
  }

  async addEncounterParticipant(campaignId, encounterId, data) {
    return this.post(`/api/campaigns/${campaignId}/encounters/${encounterId}/participants`, data);
  }

  async removeEncounterParticipant(campaignId, encounterId, participantId) {
    return this.delete(
      `/api/campaigns/${campaignId}/encounters/${encounterId}/participants/${participantId}`
    );
  }

  async getEncounter(campaignId, encounterId) {
    return this.get(`/api/campaigns/${campaignId}/encounters/${encounterId}`);
  }

  async createEncounter(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/encounters`, data);
  }

  async updateEncounter(campaignId, encounterId, data) {
    return this.put(`/api/campaigns/${campaignId}/encounters/${encounterId}`, data);
  }

  async deleteEncounter(campaignId, encounterId) {
    return this.delete(`/api/campaigns/${campaignId}/encounters/${encounterId}`);
  }

  async calculateEncounterCR(campaignId, encounterId, partySize, partyLevels) {
    return this.post(`/api/campaigns/${campaignId}/encounters/${encounterId}/calculate-cr`, {
      party_size: partySize,
      party_levels: partyLevels,
    });
  }

  async loadEncounterToCombat(campaignId, encounterId, combatId = null) {
    return this.post(`/api/campaigns/${campaignId}/encounters/${encounterId}/load-to-combat`, {
      combat_id: combatId,
    });
  }

  // Loot system endpoints
  async getLootPools(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.encounter_id) params.append('encounter_id', filters.encounter_id);
    if (filters.quest_id) params.append('quest_id', filters.quest_id);
    const query = params.toString();
    return this.get(`/api/campaigns/${campaignId}/loot-pools${query ? `?${query}` : ''}`);
  }

  async getLootPool(campaignId, poolId) {
    return this.get(`/api/campaigns/${campaignId}/loot-pools/${poolId}`);
  }

  async createLootPool(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/loot-pools`, data);
  }

  async updateLootPool(campaignId, poolId, data) {
    return this.put(`/api/campaigns/${campaignId}/loot-pools/${poolId}`, data);
  }

  async deleteLootPool(campaignId, poolId) {
    return this.delete(`/api/campaigns/${campaignId}/loot-pools/${poolId}`);
  }

  async addLootItem(campaignId, poolId, data) {
    return this.post(`/api/campaigns/${campaignId}/loot-pools/${poolId}/items`, data);
  }

  async updateLootItem(campaignId, poolId, itemId, data) {
    return this.put(`/api/campaigns/${campaignId}/loot-pools/${poolId}/items/${itemId}`, data);
  }

  async deleteLootItem(campaignId, poolId, itemId) {
    return this.delete(`/api/campaigns/${campaignId}/loot-pools/${poolId}/items/${itemId}`);
  }

  async assignLootItem(campaignId, poolId, data) {
    return this.post(`/api/campaigns/${campaignId}/loot-pools/${poolId}/assign`, data);
  }

  async distributeLoot(campaignId, poolId, data) {
    return this.post(`/api/campaigns/${campaignId}/loot-pools/${poolId}/distribute`, data);
  }

  async getPartyResources(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/party-resources`);
  }

  async updatePartyResource(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/party-resources`, data);
  }

  async deletePartyResource(campaignId, resourceId) {
    return this.delete(`/api/campaigns/${campaignId}/party-resources/${resourceId}`);
  }

  async recordExpense(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/party-resources/expense`, data);
  }

  async depositToPartyStash(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/stash/deposit`, data);
  }

  async previewLootPool(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/loot-pools/preview`, data);
  }

  async generateLootPool(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/loot-pools/generate`, data);
  }

  async moveLootPoolToPartyStash(campaignId, poolId) {
    return this.post(`/api/campaigns/${campaignId}/loot-pools/${poolId}/to-party-stash`);
  }

  async getSrdTreasure() {
    return this.get('/api/srd/treasure');
  }

  async rollSrdTreasure(data) {
    return this.post('/api/srd/treasure/roll', data);
  }

  async createStashRequest(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/stash/requests`, data);
  }

  async getStashRequests(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    const query = params.toString();
    return this.get(`/api/campaigns/${campaignId}/stash/requests${query ? `?${query}` : ''}`);
  }

  async resolveStashRequest(campaignId, requestId, data) {
    return this.post(`/api/campaigns/${campaignId}/stash/requests/${requestId}/resolve`, data);
  }

  // XP tracking endpoints
  async getCharacterXP(campaignId, characterId) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/xp`);
  }

  async awardXP(campaignId, characterId, data) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/xp/award`, data);
  }

  async awardXPParty(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/xp/award-party`, data);
  }

  async getXPAwards(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.character_id) params.append('character_id', filters.character_id);
    if (filters.award_type) params.append('award_type', filters.award_type);
    if (filters.limit) params.append('limit', filters.limit);
    const query = params.toString();
    return this.get(`/api/campaigns/${campaignId}/xp/awards${query ? `?${query}` : ''}`);
  }

  async setCharacterXP(campaignId, characterId, data) {
    return this.put(`/api/campaigns/${campaignId}/characters/${characterId}/xp`, data);
  }

  async getLevelProgress(campaignId, characterId) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/level-progress`);
  }

  async getXPSettings(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/xp-settings`);
  }

  async updateXPSettings(campaignId, data) {
    return this.put(`/api/campaigns/${campaignId}/xp-settings`, data);
  }

  // Calendar & Time Tracking endpoints
  async getCampaignCalendar(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/calendar`);
  }

  async createOrUpdateCalendar(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/calendar`, data);
  }

  async setCurrentDate(campaignId, currentDate) {
    return this.put(`/api/campaigns/${campaignId}/calendar/current-date`, { current_date: currentDate });
  }

  async advanceTime(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/calendar/advance`, data);
  }

  async getCalendarEvents(campaignId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.event_type) params.append('event_type', filters.event_type);
    const query = params.toString();
    return this.get(`/api/campaigns/${campaignId}/calendar/events${query ? `?${query}` : ''}`);
  }

  async createCalendarEvent(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/calendar/events`, data);
  }

  async updateCalendarEvent(campaignId, eventId, data) {
    return this.put(`/api/campaigns/${campaignId}/calendar/events/${eventId}`, data);
  }

  async deleteCalendarEvent(campaignId, eventId) {
    return this.delete(`/api/campaigns/${campaignId}/calendar/events/${eventId}`);
  }

  async recordTimePassage(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/time-tracking`, data);
  }

  async getTimeTracking(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/time-tracking`);
  }

  // Rest tracking endpoints
  async startRest(campaignId, characterId, restType) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/rest/start`, {
      rest_type: restType,
    });
  }

  async completeRest(campaignId, characterId, restId, data) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/rest/complete`, {
      rest_id: restId,
      ...data,
    });
  }

  async getRestHistory(campaignId, characterId) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/rest/history`);
  }

  // Inspiration & Advantage endpoints
  async getInspiration(campaignId, characterId) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/inspiration`);
  }

  async grantInspiration(campaignId, characterId, notes) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/inspiration/grant`, {
      notes,
    });
  }

  async revokeInspiration(campaignId, characterId) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/inspiration/revoke`);
  }

  async useInspiration(campaignId, characterId) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/inspiration/use`);
  }

  async getAdvantage(campaignId, characterId) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/advantage`);
  }

  async applyAdvantage(campaignId, characterId, data) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/advantage`, data);
  }

  async removeAdvantage(campaignId, characterId, advantageId) {
    return this.delete(`/api/campaigns/${campaignId}/characters/${characterId}/advantage/${advantageId}`);
  }

  // Spell & Ability Usage endpoints
  async getSpellSlots(campaignId, characterId) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/spell-slots`);
  }

  async useSpellSlot(campaignId, characterId, data) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/spell-slots/use`, data);
  }

  /** Apply healing to a campaign-linked character (party Cure Wounds, etc.). */
  async healCampaignCharacter(campaignId, characterId, data) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/heal`, data);
  }

  async recoverSpellSlots(campaignId, characterId, data) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/spell-slots/recover`, data);
  }

  async getAbilities(campaignId, characterId) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/abilities`);
  }

  async useAbility(campaignId, characterId, abilityName) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/abilities/${abilityName}/use`);
  }

  async recoverAbilities(campaignId, characterId, rechargeType) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/abilities/recover`, {
      recharge_type: rechargeType,
    });
  }

  async getSpellHistory(campaignId, characterId, limit = 50) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/spell-history?limit=${limit}`);
  }

  // Standalone spellbook endpoints
  async getSpellbooks() {
    return this.get('/api/spellbooks');
  }

  async createSpellbook(data) {
    return this.post('/api/spellbooks', data);
  }

  async updateSpellbook(spellbookId, data) {
    return this.patch(`/api/spellbooks/${spellbookId}`, data);
  }

  async deleteSpellbook(spellbookId) {
    return this.delete(`/api/spellbooks/${spellbookId}`);
  }

  async addSpellToSpellbook(spellbookId, spell) {
    return this.post(`/api/spellbooks/${spellbookId}/spells`, { spell });
  }

  async removeSpellFromSpellbook(spellbookId, spellId) {
    return this.delete(`/api/spellbooks/${spellbookId}/spells/${encodeURIComponent(String(spellId))}`);
  }

  async updateSpellbookSlot(spellbookId, data) {
    return this.post(`/api/spellbooks/${spellbookId}/slots`, data);
  }

  async resetSpellbookSlots(spellbookId) {
    return this.post(`/api/spellbooks/${spellbookId}/slots/reset`, {});
  }

  async castSpellFromSpellbook(spellbookId, data) {
    return this.post(`/api/spellbooks/${spellbookId}/cast`, data);
  }

  // Encounter Tables endpoints
  async getEncounterTables(campaignId, environment) {
    const url = environment
      ? `/api/campaigns/${campaignId}/encounter-tables?environment=${environment}`
      : `/api/campaigns/${campaignId}/encounter-tables`;
    return this.get(url);
  }

  async createEncounterTable(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/encounter-tables`, data);
  }

  async getEncounterTable(campaignId, tableId) {
    return this.get(`/api/campaigns/${campaignId}/encounter-tables/${tableId}`);
  }

  async updateEncounterTable(campaignId, tableId, data) {
    return this.put(`/api/campaigns/${campaignId}/encounter-tables/${tableId}`, data);
  }

  async deleteEncounterTable(campaignId, tableId) {
    return this.delete(`/api/campaigns/${campaignId}/encounter-tables/${tableId}`);
  }

  async addEncounterTableEntry(campaignId, tableId, data) {
    return this.post(`/api/campaigns/${campaignId}/encounter-tables/${tableId}/entries`, data);
  }

  async updateEncounterTableEntry(campaignId, tableId, entryId, data) {
    return this.put(`/api/campaigns/${campaignId}/encounter-tables/${tableId}/entries/${entryId}`, data);
  }

  async deleteEncounterTableEntry(campaignId, tableId, entryId) {
    return this.delete(`/api/campaigns/${campaignId}/encounter-tables/${tableId}/entries/${entryId}`);
  }

  async rollEncounterTable(campaignId, tableId) {
    return this.post(`/api/campaigns/${campaignId}/encounter-tables/${tableId}/roll`);
  }

  async rollAndLoadEncounter(campaignId, tableId) {
    return this.post(`/api/campaigns/${campaignId}/encounter-tables/${tableId}/roll-and-load`);
  }

  // Relationship Mapping endpoints
  async getNPCRelationships(campaignId, npcId) {
    return this.get(`/api/campaigns/${campaignId}/npcs/${npcId}/relationships`);
  }

  async createNPCRelationship(campaignId, npcId1, npcId2, data) {
    return this.post(`/api/campaigns/${campaignId}/npcs/${npcId1}/relationships/${npcId2}`, data);
  }

  async getCharacterRelationships(campaignId, characterId) {
    return this.get(`/api/campaigns/${campaignId}/characters/${characterId}/relationships`);
  }

  async createCharacterRelationship(campaignId, characterId, npcId, data) {
    return this.post(`/api/campaigns/${campaignId}/characters/${characterId}/relationships/${npcId}`, data);
  }

  async getRelationshipGraph(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/relationship-graph`);
  }

  // Faction System endpoints
  async getFactions(campaignId) {
    return this.get(`/api/campaigns/${campaignId}/factions`);
  }

  async createFaction(campaignId, data) {
    return this.post(`/api/campaigns/${campaignId}/factions`, data);
  }

  async getFaction(campaignId, factionId) {
    return this.get(`/api/campaigns/${campaignId}/factions/${factionId}`);
  }

  async updateFaction(campaignId, factionId, data) {
    return this.put(`/api/campaigns/${campaignId}/factions/${factionId}`, data);
  }

  async deleteFaction(campaignId, factionId) {
    return this.delete(`/api/campaigns/${campaignId}/factions/${factionId}`);
  }

  async addFactionMember(campaignId, factionId, data) {
    return this.post(`/api/campaigns/${campaignId}/factions/${factionId}/members`, data);
  }

  async updateFactionReputation(campaignId, factionId, data) {
    return this.post(`/api/campaigns/${campaignId}/factions/${factionId}/reputation`, data);
  }

  // Homebrew endpoints
  async getHomebrew(type, campaignId) {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (campaignId) params.append('campaign_id', campaignId);
    return this.get(`/api/homebrew${params.toString() ? '?' + params.toString() : ''}`);
  }

  async getHomebrewEntry(id) {
    return this.get(`/api/homebrew/${id}`);
  }

  async createHomebrew(data) {
    return this.post('/api/homebrew', data);
  }

  async updateHomebrew(id, data) {
    return this.put(`/api/homebrew/${id}`, data);
  }

  async deleteHomebrew(id) {
    return this.delete(`/api/homebrew/${id}`);
  }

  async exportHomebrew(id) {
    return this.get(`/api/homebrew/${id}/export`);
  }

  async importHomebrew(data) {
    return this.post('/api/homebrew/import', data);
  }

  async getHomebrewShareCodes(homebrewId) {
    return this.get(`/api/homebrew/${homebrewId}/share-codes`);
  }

  async createHomebrewShareCode(homebrewId, data) {
    return this.post(`/api/homebrew/${homebrewId}/share-codes`, data);
  }

  async redeemHomebrewShareCode(code) {
    return this.post('/api/homebrew/share-codes/redeem', { code });
  }

  // SRD data endpoints
  async getSpells(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/api/srd/spells${query ? `?${query}` : ''}`);
  }

  async getMonsters(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/api/srd/monsters${query ? `?${query}` : ''}`);
  }

  async getMonsterById(monsterId) {
    return this.get(`/api/srd/monsters/${monsterId}`);
  }

  async getItems(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/api/srd/items${query ? `?${query}` : ''}`);
  }

  async getClasses() {
    return this.get('/api/srd/classes');
  }

  async getRaces() {
    return this.get('/api/srd/races');
  }

  async getBackgrounds() {
    return this.get('/api/srd/backgrounds');
  }

  async getSubclasses(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.get(`/api/srd/subclasses${query ? `?${query}` : ''}`);
  }
}

export const apiClient = new ApiClient();
